import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Activity, RefreshCw, Search } from 'lucide-react';
import {
  appAdminListActivityLogs, appAdminActivityFacets, formatApiErrorDetail,
} from '@/lib/appApi';

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  'property.created': 'Property created',
  'unit.created': 'Unit created',
  'tenant.created': 'Tenant added',
  'tenant.updated': 'Tenant updated',
  'tenant.deleted': 'Tenant removed',
  'transaction.updated': 'Transaction updated',
  'message.posted': 'Message posted',
  'door_code.created': 'Door code added',
  'door_code.updated': 'Door code updated',
  'door_code.deleted': 'Door code deleted',
  'marketing.created': 'Marketing link added',
  'marketing.updated': 'Marketing link updated',
  'marketing.deleted': 'Marketing link deleted',
  'listing.updated': 'Listing edited',
  'user.invited': 'User invited',
  'user.updated': 'User updated',
  'user.revoked': 'User access revoked',
};

const ACTION_COLORS = {
  created: 'bg-emerald-100 text-emerald-700',
  added: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  edited: 'bg-blue-100 text-blue-700',
  posted: 'bg-violet-100 text-violet-700',
  deleted: 'bg-rose-100 text-rose-700',
  removed: 'bg-rose-100 text-rose-700',
  revoked: 'bg-rose-100 text-rose-700',
  invited: 'bg-amber-100 text-amber-700',
};

function actionBadge(action) {
  const label = ACTION_LABELS[action] || action;
  const verb = label.split(' ').pop().toLowerCase();
  const cls = ACTION_COLORS[verb] || 'bg-gray-100 text-gray-700';
  return <Badge className={`${cls} font-medium border-0 text-[10px] uppercase tracking-wider`}>{label}</Badge>;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ actors: [], actions: [], resource_types: [] });
  const [filters, setFilters] = useState({ actor_id: '', action: '', resource_type: '', date_from: '', date_to: '' });
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const queryParams = useMemo(() => {
    const p = { limit: PAGE_SIZE, skip };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, skip]);

  const load = async () => {
    setLoading(true);
    try {
      const [data, f] = await Promise.all([
        appAdminListActivityLogs(queryParams),
        // Only refresh facets on first load — they rarely change
        facets.actions.length === 0 ? appAdminActivityFacets() : Promise.resolve(facets),
      ]);
      setLogs(data.logs);
      setTotal(data.total);
      setFacets(f);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Failed to load activity log');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [queryParams]);

  const setFilter = (k, v) => { setSkip(0); setFilters((f) => ({ ...f, [k]: v })); };
  const clearFilters = () => { setSkip(0); setFilters({ actor_id: '', action: '', resource_type: '', date_from: '', date_to: '' }); setSearch(''); };

  const filteredLogs = useMemo(() => {
    if (!search.trim()) return logs;
    const q = search.toLowerCase();
    return logs.filter((l) =>
      (l.summary || '').toLowerCase().includes(q) ||
      (l.actor_name || '').toLowerCase().includes(q) ||
      (l.action || '').toLowerCase().includes(q),
    );
  }, [logs, search]);

  const hasActiveFilter = !!(filters.actor_id || filters.action || filters.resource_type || filters.date_from || filters.date_to || search);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-700" /> Activity Log
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Track who did what, and when. Logs are retained for 1 year.</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => load()} data-testid="activity-log-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-xs text-gray-500">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search summary, user, action..."
                className="pl-8 h-9"
                data-testid="activity-log-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">User</Label>
            <Select value={filters.actor_id || 'all'} onValueChange={(v) => setFilter('actor_id', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="activity-log-filter-actor"><SelectValue placeholder="All users" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                {facets.actors.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Action</Label>
            <Select value={filters.action || 'all'} onValueChange={(v) => setFilter('action', v === 'all' ? '' : v)}>
              <SelectTrigger className="h-9" data-testid="activity-log-filter-action"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {facets.actions.map((a) => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)} className="h-9" data-testid="activity-log-filter-from" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)} className="h-9" data-testid="activity-log-filter-to" />
          </div>
          {hasActiveFilter && (
            <div className="lg:col-span-6 flex justify-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-7" data-testid="activity-log-clear-filters">
                Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Events</span>
            <span className="text-xs text-gray-500 font-normal">
              {loading ? 'Loading...' : `${total.toLocaleString()} total · page ${currentPage} of ${totalPages}`}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                <tr>
                  <th className="text-left py-2 px-4 w-44">When</th>
                  <th className="text-left py-2 px-4 w-44">User</th>
                  <th className="text-left py-2 px-4 w-44">Action</th>
                  <th className="text-left py-2 px-4">Summary</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={4} className="text-center py-8 text-gray-400">Loading...</td></tr>
                )}
                {!loading && filteredLogs.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-12 text-gray-400">
                    {hasActiveFilter ? 'No events match the current filters.' : 'No activity logged yet.'}
                  </td></tr>
                )}
                {!loading && filteredLogs.map((l) => (
                  <tr
                    key={l.id}
                    className="border-t hover:bg-gray-50/60 cursor-pointer"
                    onClick={() => setSelected(l)}
                    data-testid={`activity-log-row-${l.id}`}
                  >
                    <td className="py-2.5 px-4 text-xs text-gray-500 whitespace-nowrap">{formatTimestamp(l.created_at)}</td>
                    <td className="py-2.5 px-4">
                      <div className="font-medium text-gray-800">{l.actor_name || 'Unknown'}</div>
                      {l.actor_email && <div className="text-[11px] text-gray-400">{l.actor_email}</div>}
                    </td>
                    <td className="py-2.5 px-4">{actionBadge(l.action)}</td>
                    <td className="py-2.5 px-4 text-gray-700">{l.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-xs text-gray-500">Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total.toLocaleString()}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="activity-log-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="activity-log-next">Next</Button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Event detail</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm" data-testid="activity-log-detail">
              <DetailRow label="When" value={formatTimestamp(selected.created_at)} />
              <DetailRow label="User" value={`${selected.actor_name}${selected.actor_email ? ` (${selected.actor_email})` : ''}`} />
              <DetailRow label="Role" value={selected.actor_role || '—'} />
              <DetailRow label="Action" value={ACTION_LABELS[selected.action] || selected.action} />
              <DetailRow label="Resource" value={`${selected.resource_type || '—'}${selected.resource_id ? ` · ${selected.resource_id}` : ''}`} />
              <DetailRow label="Summary" value={selected.summary} />
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Metadata</div>
                  <pre className="bg-gray-50 rounded-lg p-3 text-[11px] text-gray-700 overflow-auto max-h-72 whitespace-pre-wrap">
                    {JSON.stringify(selected.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-[120px,1fr] gap-3 items-start">
      <span className="text-xs text-gray-500 uppercase tracking-wider pt-0.5">{label}</span>
      <span className="text-gray-800 break-words">{value}</span>
    </div>
  );
}
